import { h, Component } from 'preact';
import { observer } from 'mobx-preact';
import { Text, Localizer } from 'preact-i18n';

import Input from '../../components/form/input';
import { compareTextTemplates } from '../../lib/textTemplateSort';
import {
    fillMergeFields, countSmsSegments, isActiveStatus, isEmailChannel,
} from '../../lib/smsBulk';
import {
    SmsBulkSenderLine, SmsBulkTemplates, SmsBulkTestButton, SmsBulkProgress, DEFAULT_SENDER,
} from './smsBulkParts';

const POLL_MS = 3000;

/*
 * Utsendelse: send one SMS or one e-mail to every visitor in the current
 * Besøkende list. Opened from routes/visitor/loggedIn.js with
 * { search, selfRegisteredOnly, channel }. The backend resolves the recipients
 * (same query as the CSV export) and a cron worker sends them; this drawer only
 * previews, creates, polls and cancels.
 */
@observer
class DyrejournalSendBulkSms extends Component {
    constructor(props) {
        super(props);
        this.state = {
            step: 'compose',
            preview: null,
            previewLoading: true,
            textTemplates: [],
            showTemplates: false,
            confirmed: false,
            inProgress: false,
            testInProgress: false,
            testSent: false,
            error: '',
            job: null,
        };
        this.pollTimer = null;
    }

    componentDidMount() {
        const { smsBulkStore } = this.props.stores;
        smsBulkStore.resetNewSmsBulk();
        this.loadPreview();
        if (!this.isEmail()) {
            this.loadTemplates();
        }
    }

    componentWillUnmount() {
        this.stopPolling();
    }

    isEmail = () => isEmailChannel(this.props.channel)

    channel = () => (this.isEmail() ? 'email' : 'sms')

    source = () => (this.props.selfRegisteredOnly ? 'selfRegistered' : '')

    loadPreview = async () => {
        const { visitorStore } = this.props.stores;
        const { search = '' } = this.props;
        const response = await visitorStore.bulkSmsPreview({ search, source: this.source(), channel: this.channel() });
        if (response && response.status === 200) {
            this.setState({ preview: response.data, previewLoading: false });
        } else {
            this.setState({ previewLoading: false, error: 'Kunne ikke hente mottakere. Prøv igjen.' });
        }
    }

    loadTemplates = async () => {
        const { textTemplateStore } = this.props.stores;
        const tpls = await textTemplateStore.load({ query: { section: 'sendSms' }, skipUpdate: 1 });
        this.setState({ textTemplates: Array.isArray(tpls) ? [...tpls].sort(compareTextTemplates) : [] });
    }

    toggleTemplates = () => {
        this.setState(prev => ({ showTemplates: !prev.showTemplates }));
    }

    applyTemplate = (e) => {
        const { smsBulkStore } = this.props.stores;
        const { body } = e.currentTarget.dataset;
        smsBulkStore.updateObjectKeyValue('newSmsBulk', 'body', body || '');
        this.setState({ showTemplates: false });
    }

    toggleConfirmed = (e) => {
        this.setState({ confirmed: !!e.target.checked });
    }

    sendTest = async () => {
        const { appState, userStore, smsBulkStore } = this.props.stores;
        const { user = {}, customer = {} } = userStore;
        const { newSmsBulk = {} } = smsBulkStore;
        if (!newSmsBulk.body) {
            return;
        }
        const me = { firstname: user.firstname, lastname: user.lastname };
        this.setState({ testInProgress: true, testSent: false });
        let response;
        if (this.isEmail()) {
            if (!user.email) {
                this.setState({ testInProgress: false });
                return;
            }
            response = await appState.sendEmail({
                to: [user.email],
                subject: fillMergeFields(newSmsBulk.subject || '', me),
                body: fillMergeFields(newSmsBulk.body, me),
                format: 'markdown',
            });
        } else {
            if (!user.cellphone) {
                this.setState({ testInProgress: false });
                return;
            }
            response = await appState.sendSms({
                from: customer.smsSender || DEFAULT_SENDER,
                to: [user.cellphone],
                body: fillMergeFields(newSmsBulk.body, me),
            });
        }
        this.setState({ testInProgress: false, testSent: !!(response && response.status === 200) });
    }

    send = async () => {
        const { visitorStore, smsBulkStore } = this.props.stores;
        const { newSmsBulk = {} } = smsBulkStore;
        const { search = '' } = this.props;
        this.setState({ inProgress: true, error: '' });
        const response = await visitorStore.bulkSmsCreate({
            search,
            source: this.source(),
            body: newSmsBulk.body,
            channel: this.channel(),
            subject: newSmsBulk.subject || '',
        });
        if (response && response.status === 201) {
            const job = Array.isArray(response.data) ? response.data[0] : response.data;
            this.setState({ step: 'sending', job, inProgress: false });
            this.startPolling(job.id);
            return;
        }
        let message = 'Kunne ikke starte utsendelsen. Prøv igjen.';
        if (response && response.status === 409) {
            message = 'En utsendelse pågår allerede. Vent til den er ferdig.';
        } else if (response && response.message) {
            message = response.message;
        }
        this.setState({ inProgress: false, error: message });
    }

    startPolling = (id) => {
        this.stopPolling();
        this.pollTimer = setInterval(() => this.poll(id), POLL_MS);
    }

    stopPolling = () => {
        if (this.pollTimer) {
            clearInterval(this.pollTimer);
            this.pollTimer = null;
        }
    }

    poll = async (id) => {
        const { smsBulkStore } = this.props.stores;
        const job = await smsBulkStore.loadOne(id);
        if (!job) {
            return;
        }
        if (isActiveStatus(job.status)) {
            this.setState({ job });
        } else {
            this.stopPolling();
            this.setState({ job, step: 'done' });
        }
    }

    cancel = async () => {
        const { smsBulkStore } = this.props.stores;
        const { job } = this.state;
        if (!job) {
            return;
        }
        const response = await smsBulkStore.cancel(job.id);
        if (response && response.status === 200) {
            const updated = Array.isArray(response.data) ? response.data[0] : response.data;
            this.setState({ job: updated || { ...job, status: 'cancelled' } });
        }
    }

    close = () => {
        const { appState } = this.props.stores;
        this.stopPolling();
        appState.toggleDrawer();
    }

    renderFilter() {
        const { search = '', selfRegisteredOnly } = this.props;
        return (
            <div class='text-body-secondary small mb-2'>
                <i class='fa-duotone fa-filter me-1' />
                {search
                    ? <Text id='dyrejournal.sms-bulk-filter-search' fields={{ search }}>Search: "{search}"</Text>
                    : <Text id='dyrejournal.sms-bulk-filter-all'>All visitors</Text>}
                {selfRegisteredOnly && <>, <Text id='dyrejournal.sms-bulk-filter-self'>self-registered only</Text></>}
            </div>
        );
    }

    renderCounts(preview) {
        const isEmail = this.isEmail();
        return (
            <div class='card mb-3'>
                <div class='card-body py-2 px-3'>
                    <div class='d-flex align-items-baseline'>
                        <span class='display-6 me-2'>{preview.recipients}</span>
                        {isEmail
                            ? <Text id='dyrejournal.sms-bulk-recipients-email'>recipients with a valid e-mail address</Text>
                            : <Text id='dyrejournal.sms-bulk-recipients'>recipients with a valid mobile</Text>}
                    </div>
                    <div class='small text-body-secondary'>
                        <Text id='dyrejournal.sms-bulk-matched' fields={{ count: preview.matched }}>{preview.matched} in the list</Text>
                        {!isEmail && preview.skippedNoMobile > 0 && <>, <Text id='dyrejournal.sms-bulk-skipped-nomobile' fields={{ count: preview.skippedNoMobile }}>{preview.skippedNoMobile} without a mobile</Text></>}
                        {isEmail && preview.skippedNoEmail > 0 && <>, <Text id='dyrejournal.sms-bulk-skipped-noemail' fields={{ count: preview.skippedNoEmail }}>{preview.skippedNoEmail} without an e-mail address</Text></>}
                        {preview.skippedReserved > 0 && <>, <Text id='dyrejournal.sms-bulk-skipped-reserved' fields={{ count: preview.skippedReserved }}>{preview.skippedReserved} reserved</Text></>}
                        {preview.skippedDuplicate > 0 && <>, <Text id='dyrejournal.sms-bulk-skipped-duplicate' fields={{ count: preview.skippedDuplicate }}>{preview.skippedDuplicate} share a number with someone else</Text></>}
                    </div>
                </div>
            </div>
        );
    }

    renderSenderLine() {
        const { userStore } = this.props.stores;
        const { customer = {} } = userStore;
        return <SmsBulkSenderLine customer={customer} isEmail={this.isEmail()} />;
    }

    renderTemplates() {
        const { textTemplates, showTemplates } = this.state;
        if (this.isEmail()) {
            return null;
        }
        return (
            <SmsBulkTemplates
                textTemplates={textTemplates}
                showTemplates={showTemplates}
                onToggle={this.toggleTemplates}
                onApply={this.applyTemplate}
            />
        );
    }

    renderTestButton(body) {
        const { userStore } = this.props.stores;
        const { user = {} } = userStore;
        const { testInProgress, testSent } = this.state;
        return (
            <SmsBulkTestButton
                user={user}
                isEmail={this.isEmail()}
                body={body}
                testInProgress={testInProgress}
                testSent={testSent}
                onSend={this.sendTest}
            />
        );
    }

    renderCompose() {
        const { smsBulkStore } = this.props.stores;
        const { newSmsBulk = {} } = smsBulkStore;
        const {
            preview, previewLoading, confirmed, inProgress, error,
        } = this.state;
        const isEmail = this.isEmail();
        const body = newSmsBulk.body || '';
        const subject = newSmsBulk.subject || '';
        const { chars, segments } = countSmsSegments(body);
        const recipients = preview ? preview.recipients : 0;
        const hasContent = body.trim().length > 0 && (!isEmail || subject.trim().length > 0);
        const canSend = recipients > 0 && hasContent && confirmed && !inProgress;

        return (<>
            {this.renderFilter()}
            {previewLoading && <div class='text-body-secondary'>
                <i class='fa-duotone fa-spinner fa-spin me-1' />
                <Text id='dyrejournal.sms-bulk-counting'>Counting recipients...</Text>
            </div>}
            {preview && this.renderCounts(preview)}

            {this.renderSenderLine()}
            {this.renderTemplates()}

            {isEmail && <Localizer>
                <Input
                    classNames='form-control rounded-lg'
                    title={<Text id='dyrejournal.sms-bulk-subject'>Subject</Text>}
                    placeholder={<Text id='dyrejournal.sms-bulk-subject'>Subject</Text>}
                    stores={this.props.stores}
                    holdingStore={smsBulkStore}
                    object={newSmsBulk}
                    objectName={'newSmsBulk'}
                    type={'text'}
                    field={'subject'}
                    value={subject}
                    extraDataset={{
                        'data-isnew': true,
                    }}
                />
            </Localizer>}

            <Localizer>
                <Input
                    classNames='form-control rounded-lg'
                    title={isEmail ? <Text id='input.email-body'>Email content</Text> : <Text id='input.sms-body'>Sms content</Text>}
                    placeholder={isEmail ? <Text id='input.email-body'>Email content</Text> : <Text id='input.sms-body'>Sms content</Text>}
                    help={isEmail
                        ? <Text id='dyrejournal.sms-bulk-email-help'>Simple formatting (markdown) is allowed. An unsubscribe link is added at the bottom automatically.</Text>
                        : <Text id='input.sms-body-help'>Your sms content. Do NOT include links! SMS providers will stop the SMS from beeing sent.</Text>}
                    stores={this.props.stores}
                    holdingStore={smsBulkStore}
                    object={newSmsBulk}
                    objectName={'newSmsBulk'}
                    type={'textarea'}
                    field={'body'}
                    value={body}
                    extraDataset={{
                        'data-isnew': true,
                    }}
                />
            </Localizer>
            <div class='d-flex justify-content-between px-2 small text-body-secondary'>
                <span>Flettefelt: {'{{fornavn}}'}, {'{{etternavn}}'}</span>
                <span>
                    {isEmail
                        ? <Text id='dyrejournal.sms-bulk-chars' fields={{ chars }}>{chars} characters</Text>
                        : <Text id='dyrejournal.sms-bulk-segments' fields={{ chars, segments }}>{chars} characters, approx. {segments} SMS per recipient</Text>}
                </span>
            </div>

            {this.renderTestButton(body)}

            <div class='form-check mt-3'>
                <input class='form-check-input' type='checkbox' id='smsBulkConfirm' checked={confirmed} onChange={this.toggleConfirmed} />
                <label class='form-check-label' for='smsBulkConfirm'>
                    {isEmail
                        ? <Text id='dyrejournal.sms-bulk-confirm-email' fields={{ count: recipients }}>I understand this sends {recipients} e-mails</Text>
                        : <Text id='dyrejournal.sms-bulk-confirm' fields={{ count: recipients }}>I understand this sends {recipients} SMS, invoiced to the clinic</Text>}
                </label>
            </div>

            {error && <div class='alert alert-danger mt-3 py-2'>{error}</div>}

            <div class='d-flex justify-content-end gap-2 mt-3'>
                <button type='button' class='btn btn-outline-secondary rounded-pill' onClick={this.close}>
                    <Text id='dyrejournal.sms-bulk-cancel-button'>Cancel</Text>
                </button>
                <button type='button' class='btn btn-primary rounded-pill' disabled={!canSend} onClick={this.send}>
                    <i class={`fa-duotone ${inProgress ? 'fa-spinner fa-spin' : (isEmail ? 'fa-envelope' : 'fa-comment-sms')} me-1`} />
                    {isEmail
                        ? <Text id='dyrejournal.sms-bulk-send-email' fields={{ count: recipients }}>Send {recipients} e-mails</Text>
                        : <Text id='dyrejournal.sms-bulk-send' fields={{ count: recipients }}>Send {recipients} SMS</Text>}
                </button>
            </div>
        </>);
    }

    renderProgress() {
        const { job } = this.state;
        return <SmsBulkProgress job={job} onCancel={this.cancel} onClose={this.close} />;
    }

    render() {
        const { step } = this.state;
        const isEmail = this.isEmail();
        return (
            <div class='w-100 h-100 d-flex justify-content-center'>
                <div class='w-100 px-3' style='max-width: 720px;'>
                    <h5 class='mt-3 mb-2'>
                        <i class={`fa-duotone ${isEmail ? 'fa-envelope' : 'fa-comment-sms'} me-2`} />
                        {isEmail
                            ? <Text id='dyrejournal.sms-bulk-title-email'>Send e-mail to everyone in the list</Text>
                            : <Text id='dyrejournal.sms-bulk-title'>Send SMS to everyone in the list</Text>}
                    </h5>
                    {step === 'compose' ? this.renderCompose() : this.renderProgress()}
                </div>
            </div>
        );
    }
}

export default DyrejournalSendBulkSms;
